import React, { useState, useEffect } from 'react';
import { Search, RotateCcw, Filter } from 'lucide-react';
import { CustomDropdown } from '../../../components/Common/CustomDropdown';
import { getZonesAPI, getCitiesAPI, getDeposAPI, getRoutesAPI } from '../../../api/locations';

export interface WalletFilterState {
  search: string;
  zoneId: number | undefined;
  cityId: number | undefined;
  depoId: number | undefined;
  routeId: number | undefined;
  status: string;
}

interface WalletFiltersBarProps {
  filters: WalletFilterState;
  onFilterChange: (newFilters: WalletFilterState) => void;
  onReset: () => void;
}

export const WalletFiltersBar: React.FC<WalletFiltersBarProps> = ({
  filters,
  onFilterChange,
  onReset,
}) => {
  const [zones, setZones] = useState<Array<{ id: number; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: number; name: string }>>([]);
  const [depos, setDepos] = useState<Array<{ id: number; name: string }>>([]);
  const [routes, setRoutes] = useState<Array<{ id: number; name: string }>>([]);

  // Fetch location dropdown data on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const [zRes, cRes, dRes, rRes] = await Promise.all([
          getZonesAPI().catch(() => ({ data: [] })),
          getCitiesAPI().catch(() => ({ data: [] })),
          getDeposAPI().catch(() => ({ data: [] })),
          getRoutesAPI().catch(() => ({ data: [] })),
        ]);

        if (zRes && zRes.data && Array.isArray(zRes.data)) {
          setZones(zRes.data);
        }
        if (cRes && cRes.data && Array.isArray(cRes.data)) {
          setCities(cRes.data);
        }
        if (dRes && dRes.data && Array.isArray(dRes.data)) {
          setDepos(dRes.data);
        }
        if (rRes && rRes.data && Array.isArray(rRes.data)) {
          setRoutes(rRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch filter location dropdowns:', err);
      }
    };
    fetchLocations();
  }, []);

  // Format dropdown options with "All" option at top
  const zoneOptions = [{ id: '', name: 'All Zones' }, ...zones.map((z) => ({ id: z.id, name: z.name }))];
  const cityOptions = [{ id: '', name: 'All Cities' }, ...cities.map((c) => ({ id: c.id, name: c.name }))];
  const depoOptions = [{ id: '', name: 'All Depos' }, ...depos.map((d) => ({ id: d.id, name: d.name }))];
  const routeOptions = [{ id: '', name: 'All Routes' }, ...routes.map((r) => ({ id: r.id, name: r.name }))];
  const statusOptions = ['Status', 'ACTIVE', 'INACTIVE'];

  // Current display names for CustomDropdown
  const selectedZoneName = zones.find((z) => z.id === filters.zoneId)?.name || 'All Zones';
  const selectedCityName = cities.find((c) => c.id === filters.cityId)?.name || 'All Cities';
  const selectedDepoName = depos.find((d) => d.id === filters.depoId)?.name || 'All Depos';
  const selectedRouteName = routes.find((r) => r.id === filters.routeId)?.name || 'All Routes';
  const selectedStatus = filters.status || 'Status';

  return (
    <div className="bg-white rounded-2xl p-4 border border-[#ECECF2] shadow-sm mb-6 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
          <Filter className="w-4 h-4 text-[#B71C1C]" />
          <span>Filter Distributors</span>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Reset Filters</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* Search input */}
        <div className="lg:col-span-2 space-y-1.5">
          <label className="block text-gray-700 font-semibold text-xs mb-1">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search Company, SAP Code, Route, Depo..."
              value={filters.search}
              onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
              className="w-full border border-[#828282] rounded-2xl py-2 pl-9 pr-3.5 outline-none text-xs font-semibold text-gray-800 placeholder-gray-400 focus:border-[#B71C1C] transition-colors"
            />
          </div>
        </div>

        {/* Zone Dropdown */}
        <div>
          <CustomDropdown
            label="Zone"
            placeholder="All Zones"
            options={zoneOptions}
            value={selectedZoneName}
            onChange={(name: string) => {
              const matched = zones.find((z) => z.name === name);
              onFilterChange({ ...filters, zoneId: matched ? matched.id : undefined });
            }}
          />
        </div>

        {/* City Dropdown */}
        <div>
          <CustomDropdown
            label="City"
            placeholder="All Cities"
            options={cityOptions}
            value={selectedCityName}
            onChange={(name: string) => {
              const matched = cities.find((c) => c.name === name);
              onFilterChange({ ...filters, cityId: matched ? matched.id : undefined });
            }}
          />
        </div>

        {/* Depo Dropdown */}
        <div>
          <CustomDropdown
            label="Depo"
            placeholder="All Depos"
            options={depoOptions}
            value={selectedDepoName}
            onChange={(name: string) => {
              const matched = depos.find((d) => d.name === name);
              onFilterChange({ ...filters, depoId: matched ? matched.id : undefined });
            }}
          />
        </div>

        {/* Route Dropdown */}
        <div>
          <CustomDropdown
            label="Route"
            placeholder="All Routes"
            options={routeOptions}
            value={selectedRouteName}
            onChange={(name: string) => {
              const matched = routes.find((r) => r.name === name);
              onFilterChange({ ...filters, routeId: matched ? matched.id : undefined });
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-4 pt-1">
        {/* Status Dropdown */}
        <div className="w-48">
          <CustomDropdown
            label="Status"
            placeholder="Status"
            options={statusOptions}
            value={selectedStatus}
            onChange={(st: string) => {
              const val = st === 'Status' ? '' : st;
              onFilterChange({ ...filters, status: val });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default WalletFiltersBar;
